import { processListingParams } from '@/api/helpers';
import { getExceptionFromAxiosError } from '@/client/helpers';
import {
  ListLearnerCoursesRequest,
  ListLearnerCoursesResponse,
  UpdateCourseAttemptRequest,
} from '@/client/learner-course';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import {
  CourseAttempt,
  CourseStatusAttemptEnum,
  FilterableCourseAttemptStatus,
} from '../courses';
import { MessageResponseModel } from '../models';
import { QuizAnswerResponse } from '../quizzes';

export const learnerService = (client: AxiosInstance) => {
  const LEARNER_COURSE_BASE_URL = '/me/courses';

  const getLearnerCourses = async (
    params: ListLearnerCoursesRequest & {
      type?: FilterableCourseAttemptStatus;
    },
  ): Promise<ListLearnerCoursesResponse> => {
    try {
      const result = await client.get<
        ListLearnerCoursesResponse,
        AxiosResponse<ListLearnerCoursesResponse>
      >(LEARNER_COURSE_BASE_URL, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getLearnerCourseLastAttempt = async (
    courseId?: string,
  ): Promise<CourseAttempt> => {
    try {
      const result = await client.get<
        CourseAttempt,
        AxiosResponse<CourseAttempt>
      >(`${LEARNER_COURSE_BASE_URL}/${courseId}/attempt`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getLearnerCourseAttempt = async (
    courseId?: string,
    attemptId?: string,
  ): Promise<CourseAttempt> => {
    try {
      const result = await client.get<
        CourseAttempt,
        AxiosResponse<CourseAttempt>
      >(`${LEARNER_COURSE_BASE_URL}/${courseId}/attempt/${attemptId}`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createLearnerCourseNewAttempt = async (
    courseId: string,
    status: CourseStatusAttemptEnum = CourseStatusAttemptEnum.IN_PROGRESS,
  ): Promise<CourseAttempt> => {
    try {
      return (
        await client.post(`${LEARNER_COURSE_BASE_URL}/${courseId}/attempt`, {
          status,
          withCredentials: true,
        })
      ).data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateCourseAttempt = async (
    params: UpdateCourseAttemptRequest,
  ): Promise<CourseAttempt> => {
    try {
      const result = await client.patch<
        CourseAttempt,
        AxiosResponse<CourseAttempt>
      >(
        `${LEARNER_COURSE_BASE_URL}/${params.courseId}/attempt/${params.attemptId}`,
        {
          ...params.payload,
          language: params.language,
          quizAttempts: params.quizAttempts,
          updateContentLanguage: params.updateContentLanguage,
        },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const answerQuizQuestion = async (
    courseId: string,
    quizId: string,
    questionId: string,
    selectedAnswerOptions: string[],
    courseAttempt?: string,
  ): Promise<QuizAnswerResponse> => {
    try {
      const result = await client.post<QuizAnswerResponse>(
        `${LEARNER_COURSE_BASE_URL}/${courseId}/quizzes/${quizId}/questions/${questionId}/answer`,
        { selectedAnswerOptions, courseAttempt },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const answerFeedbackQuestion = async (
    courseId: string,
    questionId: string,
    scaleResponse?: number,
    textResponse?: string,
    courseAttempt?: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.put<MessageResponseModel>(
        `${LEARNER_COURSE_BASE_URL}/${courseId}/feedback/questions/${questionId}/answer`,
        { scaleResponse, textResponse, courseAttempt },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const attemptLog = async (
    message: string,
    attemptId: string,
  ): Promise<{ success: boolean }> => {
    try {
      const result = await client.post<{ success: boolean }>(
        `${LEARNER_COURSE_BASE_URL}/${attemptId}/log`,
        { message },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getLearnerCourses,
    getLearnerCourseLastAttempt,
    getLearnerCourseAttempt,
    createLearnerCourseNewAttempt,
    updateCourseAttempt,
    answerQuizQuestion,
    answerFeedbackQuestion,
    attemptLog,
  };
};
